import { FC, useState } from 'react';
import { Article } from '@mui/icons-material';
import { Box, Grid } from '@mui/material';
import { Loader, Card } from '../../../../components';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { getApprovedStatusDetails, getTenDayCompleteStatusDetails } from '../../../../fetch';
import {
  BuildStatus,
  IContractApprovedDetails,
  IContractTenDayCompleteDetails
} from '../../../../models';
import { ApprovedDetails } from './approved-details/approved-details';
import { NewBuildDetails } from './new-build-details/new-build-details';
import { TenDayCompleteDetails } from './ten-day-complete-details/ten-day-complete-details';
import { ClosingDetails } from './closing-details/closing-details';
import { AcceptedDetails } from './accepted-details/accepted-details';
import { Button as SMButton } from '../../../../components';
import { UnderContractEmailModal } from '../../under-contract/under-contract-details/send-email/under-contract-email-modal';
import { EmailType, EventType } from '../../../../models';

interface IContractAcceptanceStatusDetail {
  status: string;
  statusText: string;
  refetchContractDetails: () => void;
}

export const ContractAcceptanceStatusDetail: FC<IContractAcceptanceStatusDetail> = ({
  status,
  statusText,
  refetchContractDetails
}) => {
  const { buildId }: { buildId: string } = useParams();
  const [isSaving, setIsSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setisLoading] = useState(false);

  const { isLoading: isLoadingTenDayCompleteDetails, data: tenDayDetails } = useQuery<
    IContractTenDayCompleteDetails,
    Error
  >(['getTenDayCompleteStatusDetails', buildId], () => getTenDayCompleteStatusDetails(buildId), {
    enabled: status === BuildStatus.TenDayComplete
  });

  const {
    isLoading: isLoadingApprovedDetails,
    data: approvedDetails,
    refetch: refetchApprovedDetails
  } = useQuery<IContractApprovedDetails, Error>(
    ['getApprovedStatusDetails', buildId],
    () => getApprovedStatusDetails(buildId),
    {
      enabled: status === BuildStatus.BuildApproved
    }
  );

  const allowUserToSendContractAcceptanceNotification = () => {
    if (status === BuildStatus.ContractAccepted) {
      return true;
    }
    return false;
  };
  return (
    <>
      <Box mt={1}>
        <Card title="Details" icon={<Article />}>
          {status === BuildStatus.New && <NewBuildDetails statusText={statusText} />}
          {status === BuildStatus.BuildApproved && (
            <ApprovedDetails
              statusText={statusText}
              approvedStatusDetails={approvedDetails}
              isLoadingApprovedDetails={isLoadingApprovedDetails}
              refetchApprovedDetails={refetchApprovedDetails}
              refetchContractDetails={refetchContractDetails}
              isSaving={isSaving}
              setIsSaving={setIsSaving}
            />
          )}
          {status === BuildStatus.ContractAccepted && <AcceptedDetails statusText={statusText} />}
          {status === BuildStatus.TenDayComplete && (
            <TenDayCompleteDetails
              statusText={statusText}
              tenDayDetails={tenDayDetails}
              refetchContractDetails={refetchContractDetails}
              isLoadingTenDayCompleteDetails={isLoadingTenDayCompleteDetails}
              isSaving={isSaving}
              setIsSaving={setIsSaving}
            />
          )}
          {status === BuildStatus.Closing && <ClosingDetails statusText={statusText} />}
          {(isLoadingApprovedDetails || isLoadingTenDayCompleteDetails || isSaving || !status) && (
            <Box
              mt={1}
              minHeight="4rem"
              display="flex"
              flexDirection="column"
              justifyContent="center"
            >
              <Loader type="inline" position="centered">
                Loading...
              </Loader>
            </Box>
          )}
          {allowUserToSendContractAcceptanceNotification() && (
            <Grid container spacing={2}>
              <Grid mt={theme => theme.spacing(0.5)} item xs={12}>
                <SMButton
                  id="build-info-email"
                  color="secondary"
                  variant="contained"
                  onClick={() => setIsModalOpen(true)}
                >
                  {'Send Contract Notification'}
                </SMButton>
              </Grid>
            </Grid>
          )}
        </Card>
      </Box>
      <UnderContractEmailModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        setIsLoading={() => setisLoading(false)}
        isLoading={isLoading}
        buildId={buildId}
        emailType={EmailType.ContractAccepted}
        eventType={EventType.ContractAccepted}
      />
    </>
  );
};
